import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Routes from 'Routes';
import { selectLocale } from 'models/localization';
import { useSelector } from 'react-redux';
import findAvailableLocale from 'models/helpers/findAvailableLocale';

export interface MobileLobbyViewItemSettings {
  show: boolean;
  text?: string;
  redirectExternal?: boolean;
  name?: string;
  backgroundImageUrl?: string;
  redirectUrl?: string;
  showText?: boolean;
  showLink?: boolean;
}

const defaultMobileLobbyConfig = {
  showLink: true,
  showText: true,
  backgroundImage: {},
};

const useMobileLobbyViewItems = () => {
  const { t } = useTranslation();
  const locale = useSelector(selectLocale);
  const {
    pageNames: {
      webinar = {},
      hall = {},
      participant = {},
      custom = {},
      products = {},
      speakers = {},
    } = {},
    hall: {
      multipleHall: multipleHallConfig = [],
    } = {},
    appBarConfig,
    lobby: {
      mobileHallConfig = defaultMobileLobbyConfig,
      mobileWebinarConfig = defaultMobileLobbyConfig,
      mobileParticipantConfig = defaultMobileLobbyConfig,
      mobileCustomPageConfig = defaultMobileLobbyConfig,
      mobileProductPageConfig = defaultMobileLobbyConfig,
      mobileSpeakersPageConfig = defaultMobileLobbyConfig,
      mobileCustomPageEntries = [],
    },
    features,
  } = useSelector((s) => s.eventConfigs);

  const multipleHall = (multipleHallConfig || []).map(({
    label,
    hallBackground: hallBg,
    redirectUrl,
    hallName: subHallName,
  }, hallIndex) => ({
    name: subHallName,
    text: findAvailableLocale(label || {}, locale) || `${t('page.hall', 'Exhibition Hall')} ${hallIndex + 1}`,
    showText: mobileHallConfig?.showText,
    redirectExternal: false,
    redirectUrl: `${findAvailableLocale(redirectUrl || {}, locale)}${subHallName && `&hallName=${subHallName}`}`,
    show: (mobileHallConfig?.showLink ?? true) && !features.includes('hideHall') && features.includes('multipleHall'),
    backgroundImageUrl: findAvailableLocale(hallBg || {}, locale) || findAvailableLocale(mobileHallConfig?.backgroundImage || {}, locale),
    hallBg: hallBg[locale] || Object.values(hallBg).find((it) => it !== ''),
  }));

  const localizedMobileCustomPageEntries = useMemo(
    () => mobileCustomPageEntries.map((entry) => ({
      backgroundImageUrl: findAvailableLocale(entry.backgroundImageUrl || {}, locale),
      text: findAvailableLocale(entry.text || {}, locale),
      name: 'custom',
      showText: mobileCustomPageConfig?.showText ?? true,
      show: (mobileCustomPageConfig?.showLink ?? true) && features.includes('multipleCustomPages'),
      redirectUrl: findAvailableLocale(entry.redirectUrl || {}, locale),
      redirectExternal: !!entry.redirectExternal,
    })),
    [mobileCustomPageEntries, locale, mobileCustomPageConfig?.showLink, mobileCustomPageConfig?.showText, features],
  );

  const mobileLobbyLinkList = [
    {
      ...mobileHallConfig,
      show: (mobileHallConfig?.showLink ?? true) && !features.includes('hideHall') && !features.includes('multipleHall'),
      text: findAvailableLocale(hall || {}, locale) || t('page.hall', 'Exhibition Hall'),
      name: 'hall',
      redirectExternal: false,
      backgroundImageUrl: findAvailableLocale(mobileHallConfig?.backgroundImage || {}, locale),
      redirectUrl: Routes.hall,
    },
    ...multipleHall,
    {
      ...mobileWebinarConfig,
      show: (mobileWebinarConfig?.showLink ?? true) && !features.includes('hideWebinar'),
      text: findAvailableLocale(webinar || {}, locale) || t('page.webinar', 'Webinars'),
      name: 'webinar',
      redirectExternal: false,
      backgroundImageUrl: findAvailableLocale(mobileWebinarConfig?.backgroundImage || {}, locale),
      redirectUrl: Routes.webinar,
    },
    {
      ...mobileParticipantConfig,
      show: (mobileParticipantConfig?.showLink ?? true) && !features.includes('hideParticipantPage'),
      text: findAvailableLocale(participant || {}, locale) || t('page.participants', 'Participants'),
      name: 'participants',
      redirectExternal: false,
      backgroundImageUrl: findAvailableLocale(mobileParticipantConfig?.backgroundImage || {}, locale),
      redirectUrl: Routes.participant,
    },
    {
      ...mobileProductPageConfig,
      show: (mobileProductPageConfig?.showLink ?? true) && features.includes('productPage'),
      text: findAvailableLocale(products || {}, locale) || t('page.products', 'Products'),
      name: 'products',
      redirectExternal: false,
      backgroundImageUrl: findAvailableLocale(mobileProductPageConfig?.backgroundImage || {}, locale),
      redirectUrl: Routes.products,
    },
    {
      ...mobileSpeakersPageConfig,
      show: (mobileSpeakersPageConfig?.showLink ?? true) && features.includes('speakersPage'),
      text: findAvailableLocale(speakers, locale) || t('page.speakers', 'Speakers'),
      name: 'speakers',
      redirectExternal: false,
      backgroundImageUrl: findAvailableLocale(mobileSpeakersPageConfig?.backgroundImage || {}, locale),
      redirectUrl: Routes.speakers,
    },
    {
      ...mobileCustomPageConfig,
      show: (mobileCustomPageConfig?.showLink ?? true) && features.includes('customPage'),
      text: findAvailableLocale(custom || {}, locale) || t('page.custom', 'Custom Page'),
      name: 'custom',
      redirectExternal: false,
      backgroundImageUrl: findAvailableLocale(mobileCustomPageConfig?.backgroundImage || {}, locale),
      redirectUrl: Routes.custom,
    },
    ...localizedMobileCustomPageEntries,
  ]
    .filter((settings) => settings?.show)
    .sort((a, b) => (appBarConfig?.itemOrder?.[a?.name] - appBarConfig?.itemOrder?.[b?.name]));

  return useMemo(() => mobileLobbyLinkList, [mobileLobbyLinkList]) as MobileLobbyViewItemSettings[];
};

export default useMobileLobbyViewItems;
